// FILE: src/features/nmck-calculator/lib/typical-nmck.ts
// VERSION: 1.0.0
// START_MODULE_CONTRACT:
// // PURPOSE: Вычисляет «типовую НМЦК региона» — ориентировочную стоимость поста охраны
// // (1 пост, 24/7, 30-дневный месяц, спецсредства Uд1=0.05) в ₽/мес для переданного региона.
// // SCOPE: Чистая сервер-безопасная функция + константы типового сценария. Не выполняет запросов к БД.
// // INPUT: TypicalNmckRegion — { name, mrot_value, regional_coef, north_coef }.
// // OUTPUT: number — ориентировочная типовая НМЦК региона в ₽/мес.
// // KEYWORDS: DOMAIN(НМЦК): TypicalCalc; MATH(Formula): NMCC; TECH(TypeScript): PureFunction
// // LINKS: FORMULA(use-nmtck-preview.ts): идентичная математика calculateNmtckPreview
// // END_MODULE_CONTRACT

// START_CHANGE_SUMMARY:
// LAST_CHANGE: 1.0.0 — Initial implementation.
// END_CHANGE_SUMMARY

// <MEMENTO>
// 2026-09-01: Это сервер-безопасная копия расчётной логики calculateNmtckPreview
// (src/features/nmck-calculator/hooks/use-nmtck-preview.ts). Тот модуль помечен "use client"
// и импортирует useMemo из react, поэтому его нельзя безопасно вызывать из серверного
// контекста/статических SEO-страниц. Математика здесь ПОЛНОСТЬЮ идентична (почленно):
// БЗП = МРОТ / СНР; Ск = БЗП × Ктер × (1 + север/100); ФОТ_час = Ск + ночные + праздничные + резерв_отпусков;
// СВ_час = ФОТ_час × 0.302; Рп_час = (ФОТ_час + СВ_час) × (Uб + Uд); СВОХР_час = Рп + КР + П;
// НМЦК = СВОХР_час × часы × посты × Iинфл; + НДС.
// НЕ менять формулу/коэффициенты без сверки с Приказом №45 (не ослаблять основную формулу).
// При изменении use-nmtck-preview.ts / RPC calculate_nmtck — синхронизировать этот файл.
// </MEMENTO>

import type { NmckRegion } from "../actions/regions";

// Типовой сценарий расчёта «типовой НМЦК региона».
// <MEMENTO>1 пост, круглосуточно (24ч), 30-дневный месяц, спецсредства (Uд1=0.05),
// без оружия. Стандартные ставки Приказа №45: КР 20% → indirectRate 0.2,
// П 5% → profitRate 0.05, СВ 30.2% → insuranceRate 0.302, НДС 22%, инфляция 1.0.</MEMENTO>
export const TYPICAL_NMCK_SCENARIO = {
  /** Норма рабочего времени 2026 (ч/мес) — см. nmck_calendars.snr_hours */
  snrHours: 164.33,
  /** Круглосуточный пост: часы/сутки */
  hoursPerDay: 24,
  /** 30-дневный месяц */
  daysPerMonth: 30,
  /** Количество постов в типовом расчёте */
  postsCount: 1,
  /** Uб — базовый коэффициент при 24ч = 1.0 (Таблица №1 Приказа №45) */
  uBase: 1.0,
  /** Uд1 = 0.05 — спецсредства (Таблица №2 Приказа №45) */
  uDopSum: 0.05,
  /** Страховые взносы 30.2% */
  insuranceRate: 0.302,
  /** Косвенные расходы (КР) 20% */
  indirectRate: 0.2,
  /** Прибыль (П) 5% */
  profitRate: 0.05,
  /** СТСО в типовом сценарии не учитываем (закупается отдельно) */
  stsoAmount: 0,
  /** Доп. страхование не учитываем */
  insuranceAmount: 0,
  /** Индекс инфляции (не применяем) */
  inflationIndex: 1.0,
  /** НДС 22% (основная ставка 2026) */
  vatPercent: 22,
  /**
   * Ночные часы в типовом сценарии: ⅓ от 24/7 смены (22:00–06:00) — как в RPC calculate_nmtck
   * для круглосуточного поста (по умолчанию total/3). Без них «типовая НМЦК» занижена —
   * ночная надбавка +20% (ст.96 ТК, ПП №554) — обязательная составляющая реального поста.
   */
  nightHours: 240,
  /** Праздничные часы в типовом сценарии: ~1 полный праздничный день (24 ч) за 30-дневный месяц */
  holidayHours: 24,
} as const;

/**
 * Рассчитывает типовую НМЦК региона (в ₽/мес) по сценарию TYPICAL_NMCK_SCENARIO.
 * Математика идентична calculateNmtckPreview (см. MEMENTO выше).
 */
export function calculateTypicalNmck(region: Pick<NmckRegion, "mrot_value" | "regional_coef" | "north_coef">): number {
  const {
    snrHours,
    hoursPerDay,
    daysPerMonth,
    postsCount,
    uBase,
    uDopSum,
    insuranceRate,
    indirectRate,
    profitRate,
    stsoAmount,
    insuranceAmount,
    inflationIndex,
    vatPercent,
    nightHours,
    holidayHours,
  } = TYPICAL_NMCK_SCENARIO;

  const totalHours = hoursPerDay * daysPerMonth;
  const { mrot_value: mrot, regional_coef: regionalCoef, north_coef: northCoef } = region;

  // БЗП — базовая заработная плата за час
  const bzp = mrot / snrHours;
  // Ск — скорректированная часовая ставка с учётом регионального и северного коэффициентов
  const adjustedHour = bzp * regionalCoef * (1 + northCoef / 100);

  // Надбавки и резерв отпусков (доли от скорректированной ставки)
  const nightRatio = totalHours > 0 ? nightHours / totalHours : 0;
  const holidayRatio = totalHours > 0 ? holidayHours / totalHours : 0;
  const nightAdditionPerHour = 0.2 * adjustedHour * nightRatio;
  const holidayAdditionPerHour = 1.0 * adjustedHour * holidayRatio;
  const fotBeforeVacation = adjustedHour + nightAdditionPerHour + holidayAdditionPerHour;
  const vacationReservePerHour = fotBeforeVacation / 12;
  const fotPerHour = fotBeforeVacation + vacationReservePerHour;

  // Страховые взносы
  const svPerHour = fotPerHour * insuranceRate;

  // Повышающий коэффициент (Uб + Uд), без верхнего ограничения (0.05 < 0.35)
  const uTotal = uBase + uDopSum;

  // Прямые затраты, косвенные (КР), прибыль (П)
  const directPerHour = (fotPerHour + svPerHour) * uTotal;
  const indirectPerHour = directPerHour * indirectRate;
  const profitPerHour = (directPerHour + indirectPerHour) * profitRate;
  const svohrPerHour = directPerHour + indirectPerHour + profitPerHour;

  // Итоги за период × посты, инфляция, НДС
  const svohrTotal = svohrPerHour * totalHours * postsCount;
  const preInflation = svohrTotal + stsoAmount + insuranceAmount;
  const nmtckPreVat = preInflation * inflationIndex;
  const vatAmount = nmtckPreVat * (vatPercent / 100);
  const nmtckTotal = nmtckPreVat + vatAmount;

  return Math.round(nmtckTotal);
}

// START_FUNCTION_getTypicalNmckPerMonth
// START_CONTRACT:
// PURPOSE: Утилита форматирования типовой НМЦК к русскому формату «NN NNN ₽/мес».
// INPUT: value: number — типовой расчёт (₽/мес)
// OUTPUT: string — отформатированная строка для UI
// COMPLEXITY_SCORE: 1
// END_CONTRACT
export function formatTypicalNmck(value: number): string {
  // toLocaleString("ru-RU") вставляет неразрывный пробел (U+00A0) как разделитель тысяч;
  // в UI используется обычный пробел — заменяем для единообразия с остальным контентом.
  return `${value.toLocaleString("ru-RU").replace(/\u00A0/g, " ")} ₽/мес`;
}
// END_FUNCTION_getTypicalNmckPerMonth
